import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Search,
  SlidersHorizontal,
  X,
  Filter,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import moment from "moment";
import {
  FilterEvent,
  GetAllEvents,
  filterEventArrya,
} from "../../../redux/actions/Events";
import {
  TICKETS_FILTER_CITIES,
  getCityByLabel,
  isAllCities,
} from "./ticketsFilterCities";
import {
  TICKETS_SIDEBAR_CATEGORIES,
  buildTicketsCategorySearch,
} from "./ticketsCategoryCatalog";

export const TICKETS_DATE_PRESETS = [
  "Any Dates",
  "This Weekend",
  "This Week",
  "This Month",
  "Next Month",
  "Next 60 Days",
];

export const TICKETS_SORT_OPTIONS = [
  "Newest",
  "Date: Soonest",
  "Price: Low to High",
  "Price: High to Low",
];

export function TicketsListingHeader({
  eventCount,
  searchTerm,
  setSearchTerm,
  sortBy,
  setSortBy,
  onOpenMobileFilters,
  priceFilterHint,
  pageTitle = "All Events",
  categorySubtitle,
}) {
  return (
    <section className="bg-[#0F172A] border-b border-[#1F2937] py-8">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8"
      >
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.05 }}
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
        >
          <div>
            <h1 className="text-3xl font-black uppercase tracking-tight text-white">
              {pageTitle}
            </h1>
            <p className="text-white/40 text-sm mt-1">
              {categorySubtitle ? (
                <span className="block text-white/50 text-xs mb-0.5">
                  {categorySubtitle}
                </span>
              ) : null}
              {eventCount} events found
              {priceFilterHint ? (
                <span className="block text-white/30 text-xs mt-0.5">
                  {priceFilterHint}
                </span>
              ) : null}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative flex-1 sm:w-64">
              <Search className="absolute left-3 top-1/3 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search events..."
                className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-[#111827] border border-[#1F2937] text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-red-500/30"
              />
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-[#111827] border border-[#1F2937] rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-red-500/30 hidden sm:block"
            >
              {TICKETS_SORT_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={onOpenMobileFilters}
              className="lg:hidden flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#111827] border border-[#1F2937] text-white/60 text-sm"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filters
            </button>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}

export function TicketsFilterPanel({
  mobile = false,
  onCloseMobile,
  value,
  setValue,
  startdate,
  setstartdate,
  enddate,
  setenddate,
  setIsFilter,
  setpage,
  priceRange,
  setPriceRange,
  freeOnly,
  setFreeOnly,
  userLocationData,
  selectedCity,
  setSelectedCity,
  onApplyApiFilter,
  onClearApiFilter,
  activeCategoryId,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const startRef = useRef(null);
  const endRef = useRef(null);

  const applySidebarFilter = () => {
    const hasCity = !isAllCities(selectedCity);
    if (!enddate && !startdate && !hasCity) return;
    setIsFilter(true);
    setpage(1);
    const startDateFormatted = startdate
      ? moment(startdate).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD");
    const endDateFormatted = enddate
      ? moment(enddate).format("YYYY-MM-DD")
      : moment().add(60, "days").format("YYYY-MM-DD");
    const city = getCityByLabel(selectedCity);
    const locationParam = hasCity ? { lon: city.lon, lat: city.lat } : null;
    dispatch(filterEventArrya());
    dispatch(
      FilterEvent(
        startDateFormatted,
        endDateFormatted,
        1,
        locationParam,
        false
      )
    );
    onApplyApiFilter?.({
      startDate: startDateFormatted,
      endDate: endDateFormatted,
      location: locationParam,
    });
    if (mobile && onCloseMobile) onCloseMobile();
  };

  const clearSidebarFilter = () => {
    if (!startdate && !enddate && isAllCities(selectedCity)) return;
    setIsFilter(false);
    setValue("Any Dates");
    setstartdate("");
    setenddate("");
    setSelectedCity("All Cities");
    setpage(1);
    onClearApiFilter?.();
    dispatch(GetAllEvents(1, userLocationData));
    if (mobile && onCloseMobile) onCloseMobile();
  };

  const handleCategory = (cat) => {
    if (cat.categoryId == null) {
      navigate("/tickets");
    } else {
      navigate(
        buildTicketsCategorySearch({ id: cat.categoryId, name: cat.name })
      );
    }
    if (mobile && onCloseMobile) onCloseMobile();
  };

  return (
    <div>
      <div className="bg-[#111827] rounded-2xl border border-[#1F2937] p-6 space-y-6">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex items-center justify-between"
        >
          <h3 className="text-white font-black text-sm uppercase tracking-wider flex items-center gap-2">
            <Filter className="w-4 h-4 text-red-500" /> Filters
          </h3>
          {mobile && (
            <button
              type="button"
              onClick={onCloseMobile}
              className="text-white/40 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </motion.div>

        <div>
          <label className="text-xs text-white/50 uppercase tracking-wider mb-2 block">
            City
          </label>
          <select
            value={selectedCity}
            onChange={(e) => setSelectedCity(e.target.value)}
            className="w-full bg-[#0F172A] border border-[#1F2937] rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-red-500/30"
          >
            {TICKETS_FILTER_CITIES.map((c) => (
              <option key={c.label} value={c.label}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.05 }}
        >
          <label className="text-xs text-white/50 uppercase tracking-wider mb-2 block">
            Category
          </label>
          <motion.div className="space-y-1">
            {TICKETS_SIDEBAR_CATEGORIES.map((cat, index) => {
              const Icon = cat.icon;
              const isActive =
                cat.categoryId == null
                  ? activeCategoryId == null
                  : activeCategoryId === cat.categoryId;
              return (
                <motion.button
                  type="button"
                  key={cat.name}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.25, delay: 0.04 * index }}
                  onClick={() => handleCategory(cat)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
                    isActive
                      ? "bg-red-600 text-white"
                      : "text-white/60 hover:bg-white/5 hover:text-white"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {cat.name}
                </motion.button>
              );
            })}
          </motion.div>
        </motion.div>

        <div>
          <label className="text-xs text-white/50 uppercase tracking-wider mb-2 block">
            When
          </label>
          <div className="space-y-1">
            {TICKETS_DATE_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => setValue(preset)}
                className={`w-full text-left px-3 py-2.5 rounded-xl text-sm transition-all ${
                  value === preset
                    ? "bg-red-600 text-white"
                    : "text-white/60 hover:bg-white/5 hover:text-white"
                }`}
              >
                {preset.trim()}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <label className="text-xs text-white/50 uppercase tracking-wider mb-2 block">
              Start Date
            </label>
            <input
              ref={startRef}
              type="date"
              value={startdate}
              onChange={(e) => setstartdate(e.target.value)}
              onClick={() => startRef.current?.showPicker?.()}
              className="w-full bg-[#0F172A] border border-[#1F2937] rounded-xl px-3 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-red-500/30"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.12 }}
          >
            <label className="text-xs text-white/50 uppercase tracking-wider mb-2 block">
              End Date
            </label>
            <input
              ref={endRef}
              type="date"
              value={enddate}
              onChange={(e) => setenddate(e.target.value)}
              onClick={() => endRef.current?.showPicker?.()}
              className="w-full bg-[#0F172A] border border-[#1F2937] rounded-xl px-3 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-red-500/30"
            />
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.15 }}
        >
          <label className="text-xs text-white/50 uppercase tracking-wider mb-2 block">
            Price Range: ${priceRange[0]} – ${priceRange[1]}
          </label>
          <input
            type="range"
            min={0}
            max={500}
            value={priceRange[1]}
            onChange={(e) =>
              setPriceRange([priceRange[0], parseInt(e.target.value, 10)])
            }
            className="w-full accent-red-500"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.18 }}
          className="flex items-center justify-between"
        >
          <span className="text-sm text-white/60">Free Events Only</span>
          <button
            type="button"
            onClick={() => setFreeOnly(!freeOnly)}
            className={`w-11 h-6 rounded-full transition-colors relative ${
              freeOnly ? "bg-red-600" : "bg-[#1F2937]"
            }`}
          >
            <motion.div
              layout
              className={`absolute top-0.5 w-5 h-5 rounded-full bg-white ${
                freeOnly ? "left-[22px]" : "left-0.5"
              }`}
            />
          </button>
        </motion.div>

        <motion.button
          type="button"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.22 }}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          onClick={applySidebarFilter}
          className="w-full py-3 rounded-xl bg-red-600 text-white text-sm font-black uppercase tracking-wider hover:bg-red-700 transition-colors"
        >
          Apply Filters
        </motion.button>

        <button
          type="button"
          onClick={clearSidebarFilter}
          className="w-full py-2.5 rounded-xl border border-[#1F2937] text-white/50 text-sm hover:text-white hover:border-white/20 transition-colors"
        >
          Clear filters
        </button>
      </div>
    </div>
  );
}

export function TicketsMobileFilterDrawer({
  open,
  onClose,
  filterPanelProps,
}) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 lg:hidden"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", damping: 25 }}
            className="fixed left-0 top-0 bottom-0 w-80 bg-[#0F172A] z-50 p-4 overflow-y-auto lg:hidden"
          >
            <TicketsFilterPanel mobile onCloseMobile={onClose} {...filterPanelProps} />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
